/**
 * Growth events tracking (§7a GROWTH-OS-v9 — единая событийная схема роста).
 *
 * Единственный клиентский слой эмиссии событий воронки. Отправляет событие на
 * api-gateway (`POST /analytics/track`), который обогащает его серверными полями
 * (user_id псевдонимизирован, product, geo, ts) и форвардит в PostHog + пишет в БД.
 *
 * Контракт §7a: канонические события signup/activate/aha/share/invite_sent/
 * invite_accepted/paid/expand/churn. `aha` — единственное NSM-событие
 * (NSM teleclaw = число опубликованных постов, aha = первая успешная публикация).
 *
 * Правила:
 *  - fire-and-forget: трекинг НИКОГДА не блокирует UX и не бросает наверх;
 *  - `ref`/`sub_id` сквозные — берутся из URL при signup и хранятся в localStorage;
 *  - серверная эмиссия `paid` (payment-service webhook) — источник правды по деньгам;
 *    клиентский `paid` — только вспомогательный сигнал для мгновенного UI.
 */
import * as Sentry from '@sentry/react';
import { api } from '../api/client';

/** Канонические события роста (§7a). */
export type GrowthEvent =
  | 'signup'
  | 'activate'
  | 'aha'
  | 'share'
  | 'invite_sent'
  | 'invite_accepted'
  | 'paid'
  | 'expand'
  | 'churn';

export interface TrackProps {
  /** тип реализованной ценности для `aha` (напр. 'first_post_published') */
  value_type?: string;
  /** поверхность шеринга для `share` (напр. 'onboarding', 'analytics_page') */
  surface?: string;
  /** id артефакта, которым поделились (напр. channel handle) */
  artifact_id?: string;
  /** тариф для paid/expand */
  plan?: string;
  from_plan?: string;
  to_plan?: string;
  amount?: number;
  reason?: string;
  [key: string]: unknown;
}

const REF_KEY = 'teleclaw_ref';
const SUBID_KEY = 'teleclaw_sub_id';
const AGENCY_REF_KEY = 'teleclaw_agency_ref';

/**
 * Захватить `?ref=UID&sub_id=...` из URL в localStorage (вызывать один раз при
 * загрузке приложения). Сквозная атрибуция invite→signup→…→paid (§7a).
 *
 * `?agency_ref=` — ОТДЕЛЬНЫЙ параметр для Agency Partner Program (Plane #73, embiye
 * program "teleclaw-agency", commission_pct=25) — намеренно не переиспользует `ref`,
 * чтобы give-to-get и agency-атрибуция никогда не перепутались на одном signup.
 */
export function captureReferral(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    const subId = params.get('sub_id');
    const agencyRef = params.get('agency_ref');
    if (ref) localStorage.setItem(REF_KEY, ref);
    if (subId) localStorage.setItem(SUBID_KEY, subId);
    if (agencyRef) localStorage.setItem(AGENCY_REF_KEY, agencyRef);
  } catch {
    /* SSR / storage disabled — ignore */
  }
}

export function getAgencyReferral(): string | undefined {
  try {
    return localStorage.getItem(AGENCY_REF_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Текущая реф-атрибуция (для отправки на signup). */
export function getReferral(): { ref?: string; sub_id?: string } {
  try {
    return {
      ref: localStorage.getItem(REF_KEY) ?? undefined,
      sub_id: localStorage.getItem(SUBID_KEY) ?? undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Эмиссия события роста. Non-blocking, ошибки поглощаются (трекинг не должен
 * ломать продукт — guardrail §10a).
 */
export function track(event: GrowthEvent, props: TrackProps = {}): void {
  const payload = {
    event,
    ts: Date.now(),
    ...getReferral(),
    agency_ref: getAgencyReferral(),
    props,
  };

  // Хлебная крошка в Sentry/GlitchTip для дебага воронки.
  Sentry.addBreadcrumb({ category: 'growth', message: event, level: 'info', data: props });

  // Fire-and-forget: не ждём и не роняем UX. Роут — нативный /growth/track в api-gateway.
  void api.post('/growth/track', payload).catch(() => {
    /* swallow — трекинг best-effort */
  });
}
